/* Luxe Home Experience — dependency-free motion layer.
   Native scrolling is never hijacked; everything is driven from one rAF loop. */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var inEditor = !!(window.Shopify && window.Shopify.designMode);
  // Old Safari (< 16) lacks overflow: clip, so the CSS falls back to hidden, which disables sticky pinning
  var canClip = !!(window.CSS && CSS.supports && CSS.supports('overflow-x', 'clip'));

  function store(method, key, val) {
    try { return window.sessionStorage[method](key, val); } catch (e) { return null; }
  }

  function init(root) {
    if (!root || root.__lxInit) return;
    root.__lxInit = true;

    var scrollers = []; // functions run each frame while scrolling

    /* ---------- Preloader (once per visit, never in the theme editor) ---------- */
    var loader = root.querySelector('.lx-loader');
    function startPage() {
      root.classList.remove('is-waiting');
      root.querySelectorAll('.lx-hero .lx-split, .lx-hero .lx-reveal').forEach(function (el) { el.classList.add('is-in'); });
    }
    if (loader && !reduced && !inEditor && !store('getItem', 'lx-seen')) {
      root.classList.add('is-waiting');
      document.documentElement.style.overflow = 'hidden';
      var finish = function () {
        loader.classList.add('is-done');
        document.documentElement.style.overflow = '';
        store('setItem', 'lx-seen', '1');
        setTimeout(startPage, 350);
        setTimeout(function () { loader.classList.add('is-gone'); }, 1300);
      };
      var ready = new Promise(function (r) { if (document.readyState === 'complete') r(); else window.addEventListener('load', r, { once: true }); });
      var minTime = new Promise(function (r) { setTimeout(r, 2000); });
      var maxTime = new Promise(function (r) { setTimeout(r, 4000); });
      Promise.race([Promise.all([ready, minTime]), maxTime]).then(finish);
      // Coming back via the back button can restore the page mid-intro; never leave scrolling locked
      window.addEventListener('pageshow', function (e) { if (e.persisted && !loader.classList.contains('is-done')) finish(); });
    } else {
      if (loader) loader.classList.add('is-gone');
      requestAnimationFrame(startPage);
    }

    /* ---------- Reveal on enter ---------- */
    var targets = root.querySelectorAll('.lx-reveal, .lx-curtain, .lx-split');
    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-in'); });
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && !e.target.closest('.lx-hero')) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -12% 0px' });
      targets.forEach(function (el) { io.observe(el); });
    }

    /* ---------- Counters ---------- */
    var counters = root.querySelectorAll('[data-lx-count]');
    var countIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        countIO.unobserve(e.target);
        var el = e.target, end = parseFloat(el.getAttribute('data-lx-count')) || 0, t0 = null, dur = 2200;
        if (reduced) { el.textContent = end.toLocaleString(); return; }
        (function tick(t) {
          if (!t0) t0 = t;
          var p = Math.min((t - t0) / dur, 1), eased = 1 - Math.pow(1 - p, 4);
          el.textContent = Math.round(end * eased).toLocaleString();
          if (p < 1) requestAnimationFrame(tick);
        })(performance.now());
      });
    }, { threshold: 0.6 }) : null;
    counters.forEach(function (c) { countIO ? countIO.observe(c) : (c.textContent = c.getAttribute('data-lx-count')); });

    /* ---------- Manifesto: words light up as you scroll ---------- */
    var mani = root.querySelector('.lx-manifesto__text');
    if (mani && !reduced) {
      var words = mani.textContent.trim().split(/\s+/);
      mani.setAttribute('aria-label', mani.textContent.trim());
      mani.innerHTML = words.map(function (w) { return '<span class="lx-w" aria-hidden="true">' + w.replace(/</g, '&lt;') + '</span>'; }).join(' ');
      var spans = mani.querySelectorAll('.lx-w');
      scrollers.push(function (vh) {
        var r = mani.getBoundingClientRect();
        var p = (vh * 0.85 - r.top) / (r.height + vh * 0.35);
        var lit = Math.round(Math.max(0, Math.min(1, p)) * spans.length);
        for (var i = 0; i < spans.length; i++) spans[i].classList.toggle('is-lit', i < lit);
      });
    }

    /* ---------- Parallax layers ---------- */
    var layers = [].slice.call(root.querySelectorAll('[data-lx-speed]'));
    if (!reduced && layers.length) {
      scrollers.push(function (vh) {
        // Lookbook figures stack on phones and tablets, where drifting would overlap their captions
        var stacked = window.innerWidth < 990;
        layers.forEach(function (el) {
          if (stacked && el.tagName === 'FIGURE') { el.style.transform = ''; return; }
          var r = el.parentElement.getBoundingClientRect();
          if (r.bottom < -200 || r.top > vh + 200) return;
          var center = r.top + r.height / 2 - vh / 2;
          el.style.transform = 'translate3d(0,' + (center * parseFloat(el.getAttribute('data-lx-speed'))).toFixed(1) + 'px,0)';
        });
      });
    }

    /* ---------- Horizontal craft story (pinned on desktop) ---------- */
    var craft = root.querySelector('.lx-craft');
    if (craft) {
      var track = craft.querySelector('.lx-craft__track');
      var bar = craft.querySelector('.lx-craft__progress span');
      var panelImgs = craft.querySelectorAll('.lx-panel__media > *');
      var distance = 0, lastW = 0, lastH = 0;
      var layout = function () {
        // Pin only where a full panel fits on screen; phones, landscape phones and old Safari get the swipe carousel
        var isStatic = reduced || !canClip || window.innerWidth < 750 || window.innerHeight < 560;
        craft.classList.toggle('is-static', isStatic);
        lastW = window.innerWidth; lastH = window.innerHeight;
        if (isStatic) { craft.style.height = ''; distance = 0; return; }
        distance = Math.max(0, track.scrollWidth - window.innerWidth);
        craft.style.height = (distance + window.innerHeight) + 'px';
      };
      layout();
      window.addEventListener('resize', function () {
        // Touch browsers resize the viewport as the address bar slides; re-laying out then makes the page jump
        if (window.innerWidth === lastW && Math.abs(window.innerHeight - lastH) < 160) return;
        layout();
      });
      window.addEventListener('load', layout);
      scrollers.push(function (vh) {
        if (!distance) return;
        var r = craft.getBoundingClientRect();
        var p = Math.max(0, Math.min(1, -r.top / (r.height - vh)));
        track.style.transform = 'translate3d(' + (-p * distance).toFixed(1) + 'px,0,0)';
        if (bar) bar.style.transform = 'scaleX(' + p.toFixed(3) + ')';
        var vw = window.innerWidth;
        panelImgs.forEach(function (img) {
          var b = img.parentElement.getBoundingClientRect();
          var off = (b.left + b.width / 2 - vw / 2) / vw;
          img.style.transform = 'scale(1.15) translate3d(' + (off * -6).toFixed(2) + '%,0,0)';
        });
      });
    }

    /* ---------- Testimonials ---------- */
    var slides = root.querySelectorAll('.lx-quote__slide');
    var dots = root.querySelectorAll('.lx-quote__dots button');
    if (slides.length > 1) {
      var cur = 0, timer;
      var go = function (n) {
        slides[cur].classList.remove('is-active'); dots[cur] && dots[cur].setAttribute('aria-current', 'false');
        cur = (n + slides.length) % slides.length;
        slides[cur].classList.add('is-active'); dots[cur] && dots[cur].setAttribute('aria-current', 'true');
      };
      var play = function () { clearInterval(timer); if (!reduced) timer = setInterval(function () { go(cur + 1); }, 6500); };
      dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); play(); }); });
      play();
    }

    /* ---------- Custom cursor (mouse / trackpad only) ---------- */
    var cursor = root.querySelector('.lx-cursor');
    if (cursor && finePointer && !reduced && !inEditor) {
      var mx = -100, my = -100, cx = -100, cy = -100;
      cursor.classList.add('is-on');
      root.addEventListener('mousemove', function (e) { mx = e.clientX; my = e.clientY; }, { passive: true });
      root.addEventListener('mouseleave', function () { cursor.classList.remove('is-on'); });
      root.addEventListener('mouseenter', function () { cursor.classList.add('is-on'); });
      root.addEventListener('mouseover', function (e) {
        var t = e.target;
        cursor.classList.toggle('is-view', !!t.closest('.lx-card__media'));
        cursor.classList.toggle('is-big', !t.closest('.lx-card__media') && !!t.closest('a, button, input'));
      });
      (function follow() {
        cx += (mx - cx) * 0.18; cy += (my - cy) * 0.18;
        cursor.style.transform = 'translate3d(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px,0)';
        requestAnimationFrame(follow);
      })();
    }

    /* ---------- Magnetic buttons ---------- */
    if (finePointer && !reduced) {
      root.querySelectorAll('[data-lx-magnetic]').forEach(function (btn) {
        btn.addEventListener('mousemove', function (e) {
          var r = btn.getBoundingClientRect();
          btn.style.transform = 'translate(' + ((e.clientX - r.left - r.width / 2) * 0.25).toFixed(1) + 'px,' + ((e.clientY - r.top - r.height / 2) * 0.35).toFixed(1) + 'px)';
        });
        btn.addEventListener('mouseleave', function () { btn.style.transform = ''; });
        btn.style.transition = 'transform .5s cubic-bezier(.22,1,.36,1), color .6s cubic-bezier(.22,1,.36,1)';
      });
    }

    /* ---------- Scroll loop ---------- */
    if (scrollers.length) {
      var ticking = false;
      var run = function () {
        ticking = false;
        var vh = window.innerHeight;
        for (var i = 0; i < scrollers.length; i++) scrollers[i](vh);
      };
      var request = function () { if (!ticking) { ticking = true; requestAnimationFrame(run); } };
      window.addEventListener('scroll', request, { passive: true });
      document.addEventListener('scroll', request, { passive: true, capture: true }); // theme scrolls inside .page-wrapper, not window
      window.addEventListener('resize', request);
      run();
    }
  }

  function boot() { document.querySelectorAll('.lx').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  // Re-initialise when the section is edited in the Shopify theme editor
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('.lx');
    if (root) init(root);
  });
})();
